import * as React from "react";
import { AiPaymentService, type AIBalanceResponse, type AIPaymentRow } from "../../../../services/ai-payment-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaLoader from "../../../ui-kit/kuika-loader/kuika-loader";
import KuikaNumber from "../../../ui-kit/kuika-number/kuika-number";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import AiChatbotSuccessModal from "./ai-chatbot-success-modal";
import { formatCreditAmount } from "./ai-chatbot-utils";
import "./ai-chatbot-credits-panel.scss";

// ── Packages ────────────────────────────────────────────────────────
// End-user model: pay $X via Stripe, receive X × 100 credits. Credits are the
// single currency the user interacts with — chat turns burn credits based on
// the model, context length and tool usage of each request. The UI deliberately
// avoids exposing the USD equivalent of consumed credits.

interface PresetPackage {
  id: string;
  amountCents: number;
  label: string;
  popular?: boolean;
}

const PRESET_PACKAGES: PresetPackage[] = [
  { id: "p5", amountCents: 500, label: "$5" },
  { id: "p20", amountCents: 2000, label: "$20", popular: true },
  { id: "p50", amountCents: 5000, label: "$50" },
  { id: "p100", amountCents: 10000, label: "$100" }
];

const MIN_CUSTOM_CENTS = 500;
const MAX_CUSTOM_CENTS = 50000;
const CREDITS_PER_CENT = 1;

interface SliderScaleMark {
  cents: number;
  label: string;
}

// Labels are positioned by their real slider percentage (not evenly spaced) so
// dragging to a label lands on its value — otherwise `$100` would sit at ~33%
// visually while the slider only reaches $100 at ~19%, causing users to see
// stray values like $114 in the readout when they expected $100.
const SLIDER_SCALE_MARKS: SliderScaleMark[] = [
  { cents: 500, label: "$5" },
  { cents: 10000, label: "$100" },
  { cents: 25000, label: "$250" },
  { cents: 50000, label: "$500" }
];

function formatCurrencyCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface AiChatbotCreditsPanelOwnProps {
  /**
   * When false, render only the credits body (no `.kuika-settings-panel` frame,
   * no page header) — used when hosted inside the tabbed AI Chatbot shell
   * (`ai-chatbot-panel.tsx`). Default true so the embeddable buy-credits-modal
   * variant keeps rendering exactly what it always did.
   */
  showHeader?: boolean;
}

interface State {
  balance: AIBalanceResponse | null;
  loading: boolean;
  loadError: string | null;
  history: AIPaymentRow[];
  selectedPackageId: string;
  customCents: number;
  checkoutInProgress: boolean;
  /** True while we are verifying a Stripe redirect (session_id in URL) — the
   *  verify + balance round-trip takes ~2-3s on a happy path, so we need a
   *  visible spinner to avoid the "did it work?" gap. */
  verifyingPayment: boolean;
  successModal: {
    isOpen: boolean;
    addedCredits: number;
    newBalance: number | null;
  };
}

class AiChatbotCreditsPanel extends React.PureComponent<
  AiChatbotCreditsPanelOwnProps & IWithHistoryProp & IWithContextProp,
  State
> {
  state: State = {
    balance: null,
    loading: true,
    loadError: null,
    history: [],
    selectedPackageId: "p20",
    customCents: 2000,
    checkoutInProgress: false,
    // Pre-seed the verifying flag if the URL indicates a Stripe return so the
    // overlay shows up on the FIRST render — otherwise there's a ~50ms flash
    // of the normal panel before componentDidMount fires.
    verifyingPayment: typeof window !== "undefined" && new URLSearchParams(window.location.search).has("session_id"),
    successModal: { isOpen: false, addedCredits: 0, newBalance: null }
  };

  async componentDidMount() {
    await this.loadBalance();
    if (this.props.context?.userInfo?.isDomainOwner) {
      await this.checkReturnFromStripe();
    }
  }

  private loadBalance = async () => {
    try {
      const res = await AiPaymentService.GetBalance();
      this.setState({ balance: res.data, loading: false, loadError: null });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: unknown } })?.response?.data
        ? this.extractBackendError(err)
        : "We couldn't load your credit balance right now. Please refresh the page and try again.";
      this.setState({ loading: false, loadError: message });
    }
  };

  /** Pull a readable message out of an axios error — prefer the server-provided
   *  one so operators see the actual cause (e.g. missing table after a deploy
   *  without migrations). */
  private extractBackendError(err: unknown): string {
    const body = (err as { response?: { data?: unknown } })?.response?.data;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && typeof body === "object") {
      const obj = body as { error?: string; message?: string; title?: string };
      if (obj.error) return obj.error;
      if (obj.message) return obj.message;
      if (obj.title) return obj.title;
    }
    return "Something went wrong on our side. Please refresh the page and try again.";
  }

  private checkReturnFromStripe = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      // Not a Stripe return — make sure the overlay isn't stuck on.
      if (this.state.verifyingPayment) this.setState({ verifyingPayment: false });
      return;
    }

    // Remove session_id from the URL immediately so a refresh doesn't re-trigger
    // the verify flow (e.g. after a transient "payment not completed" retry
    // window). The backend's `existing` check makes re-calls idempotent, but
    // scrubbing the URL is cleaner UX.
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());

    // Show the "confirming your payment" overlay — Stripe test cards finalize
    // instantly, but verify + balance + retry window adds up to 2–3s on the
    // happy path. Without an overlay the user sees their stale balance and may
    // think nothing happened.
    this.setState({ verifyingPayment: true });

    // Stripe occasionally reports `paymentStatus: "unpaid"` immediately after
    // redirect on async methods (3-D Secure, bank redirects). Retry up to 3
    // times with backoff before giving up so the happy path with test cards
    // (which finalize instantly) still fires first-attempt.
    const MAX_ATTEMPTS = 3;
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res = await AiPaymentService.VerifyPayment(sessionId);
        const addedAmountCents = res.data?.amount ?? 0;
        const addedCredits = addedAmountCents * CREDITS_PER_CENT;
        const balanceRes = await AiPaymentService.GetBalance();
        this.setState({
          balance: balanceRes.data,
          loadError: null,
          verifyingPayment: false,
          successModal: {
            isOpen: true,
            addedCredits,
            newBalance: balanceRes.data?.creditsRemaining ?? null
          }
        });
        return;
      } catch (err: unknown) {
        lastError = err;
        const status = (err as { response?: { status?: number } })?.response?.status;
        const body = (err as { response?: { data?: unknown } })?.response?.data as
          | { paymentStatus?: string }
          | undefined;
        const isTransient = status === 400 && body?.paymentStatus && body.paymentStatus !== "paid";
        if (!isTransient || attempt === MAX_ATTEMPTS) break;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }

    // All attempts failed — surface the backend message so the user understands
    // what to do (usually "refresh in a few seconds" or contact support).
    const axiosErr = lastError as { response?: { status?: number; data?: unknown }; message?: string };
    const status = axiosErr?.response?.status;
    const body = axiosErr?.response?.data;
    const backendMsg =
      typeof body === "string"
        ? body
        : body && typeof body === "object"
          ? ((body as { error?: string; message?: string }).error ??
            (body as { error?: string; message?: string }).message)
          : undefined;
    console.warn("[AI Credits] verify failed", { status, body, err: lastError });
    this.setState({
      verifyingPayment: false,
      loadError:
        backendMsg ||
        axiosErr?.message ||
        "We couldn't confirm your payment just yet. If Stripe has already charged you, please refresh this page in a few seconds — your credits will appear automatically."
    });
  };

  private getCurrentAmountCents(): number {
    if (this.state.selectedPackageId === "custom") return this.state.customCents;
    const pkg = PRESET_PACKAGES.find((p) => p.id === this.state.selectedPackageId);
    return pkg?.amountCents ?? 0;
  }

  private handleSelectPreset = (id: string) => this.setState({ selectedPackageId: id });
  private handleSelectCustom = () => this.setState({ selectedPackageId: "custom" });

  private handleCustomSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cents = parseInt(e.target.value, 10);
    this.setState({ customCents: cents });
  };

  private handleCustomInputChange = (_: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    const dollars = typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(dollars)) {
      this.setState({ customCents: MIN_CUSTOM_CENTS });
      return;
    }
    let cents = Math.round(dollars * 100);
    if (cents < MIN_CUSTOM_CENTS) cents = MIN_CUSTOM_CENTS;
    if (cents > MAX_CUSTOM_CENTS) cents = MAX_CUSTOM_CENTS;
    this.setState({ customCents: cents });
  };

  private handleCheckout = async () => {
    const amountCents = this.getCurrentAmountCents();
    if (amountCents < MIN_CUSTOM_CENTS) return;

    this.setState({ checkoutInProgress: true });
    try {
      const origin = window.location.origin;
      const pathname = window.location.pathname;
      const successUrl = `${origin}${pathname}?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}${pathname}?cancelled=1`;
      const credits = amountCents * CREDITS_PER_CENT;
      const description = `AI Chatbot Credits (${formatCreditAmount(credits)} credits)`;

      const res = await AiPaymentService.CreateCheckout(amountCents, successUrl, cancelUrl, description);
      const checkoutUrl = typeof res.data === "string" ? res.data : "";
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      throw new Error("We couldn't open the checkout window. Please try again in a moment.");
    } catch (err: unknown) {
      // Surface the backend message so 400/500 root causes (domain-owner, stripe,
      // env guard) are visible to the user instead of a silent toast.
      const axiosErr = err as { response?: { status?: number; data?: unknown }; message?: string };
      const status = axiosErr?.response?.status;
      const body = axiosErr?.response?.data;
      const backendMsg =
        typeof body === "string"
          ? body
          : body && typeof body === "object"
            ? ((body as { error?: string; message?: string }).error ??
              (body as { error?: string; message?: string }).message)
            : undefined;
      console.warn("[AI Credits] checkout failed", { status, body, err });
      const msg = backendMsg || axiosErr?.message || "Checkout didn't go through. Please try again.";
      this.setState({ checkoutInProgress: false, loadError: msg });
      KMainFunctions.exceptionHandler(err);
    }
  };

  private handleCloseSuccessModal = () => {
    this.setState({ successModal: { isOpen: false, addedCredits: 0, newBalance: null } });
    let returnUrl: string | null = null;
    try {
      returnUrl = sessionStorage.getItem("kuika.aiChatbotReturnUrl");
      if (returnUrl) sessionStorage.removeItem("kuika.aiChatbotReturnUrl");
    } catch {
      // sessionStorage unavailable — leave user on the credits page.
    }
    if (returnUrl) {
      this.props.navigator.navigate(returnUrl);
    }
  };

  private renderHero() {
    const { balance, loading, loadError } = this.state;

    if (loadError) {
      return (
        <div className="aichat-credits__hero aichat-credits__hero--error">
          <div className="aichat-credits__hero-error-icon" aria-hidden="true">
            <KuikaSvg name="info-circle" width={24} height={24} />
          </div>
          <div>
            <div className="aichat-credits__hero-error-title">We couldn't load your credit balance</div>
            <div className="aichat-credits__hero-error-body">{loadError}</div>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="aichat-credits__hero">
          <span className="aichat-credits__hero-label">Credit balance</span>
          <div className="aichat-credits__hero-amount">
            <span className="credits">—</span>
            <span className="unit">credits</span>
          </div>
          <div className="aichat-credits__hero-sub">Fetching your current balance…</div>
        </div>
      );
    }

    if (!balance) {
      return (
        <div className="aichat-credits__hero">
          <span className="aichat-credits__hero-label">Credit balance</span>
          <div className="aichat-credits__hero-amount">
            <span className="credits">—</span>
            <span className="unit">credits</span>
          </div>
          <div className="aichat-credits__hero-sub">Balance information is temporarily unavailable.</div>
        </div>
      );
    }

    const creditsRemaining = balance.creditsRemaining;
    const creditsPurchased = balance.totalPurchasedCents; // $1 = 100 credits at purchase
    const creditsConsumed = balance.creditsConsumed;
    const starterCredits = balance.starterCreditsIncluded ?? 0;

    return (
      <div className="aichat-credits__hero">
        <span className="aichat-credits__hero-label">Available credits</span>
        <div className="aichat-credits__hero-amount">
          {/* The balance changes in place after a purchase lands, so its digits roll to the new one. */}
          <KuikaNumber className="credits" value={creditsRemaining} format={formatCreditAmount} />
          <span className="unit">credits</span>
        </div>
        <div className="aichat-credits__hero-sub">Shared across your workspace for every AI chat session.</div>
        <div className="aichat-credits__hero-meta">
          {starterCredits > 0 && (
            <div className="item">
              <div className="label">Welcome bonus</div>
              <div className="value">
                <KuikaNumber value={starterCredits} format={formatCreditAmount} />
              </div>
            </div>
          )}
          <div className="item">
            <div className="label">Total purchased</div>
            <div className="value">
              <KuikaNumber value={creditsPurchased} format={formatCreditAmount} />
            </div>
          </div>
          <div className="item">
            <div className="label">Total used</div>
            <div className="value">
              <KuikaNumber value={creditsConsumed} format={formatCreditAmount} />
            </div>
          </div>
          <div className="item">
            <div className="label">Remaining</div>
            <div className="value">
              <KuikaNumber value={creditsRemaining} format={formatCreditAmount} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderPackages() {
    const { selectedPackageId } = this.state;
    return (
      <div className="aichat-credits__packages" data-testid="aiChatbotCreditsPackageList">
        {PRESET_PACKAGES.map((pkg) => {
          const selected = selectedPackageId === pkg.id;
          const credits = pkg.amountCents * CREDITS_PER_CENT;
          return (
            <button
              key={pkg.id}
              type="button"
              className={["aichat-credits__package", selected ? "aichat-credits__package--selected" : ""]
                .filter(Boolean)
                .join(" ")}
              onClick={() => this.handleSelectPreset(pkg.id)}
            >
              {pkg.popular && <span className="popular-tag">Popular</span>}
              <div className="price">{pkg.label}</div>
              <div className="credits">{formatCreditAmount(credits)} credits</div>
            </button>
          );
        })}
        <button
          type="button"
          className={[
            "aichat-credits__package",
            selectedPackageId === "custom" ? "aichat-credits__package--selected" : ""
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={this.handleSelectCustom}
        >
          <div className="price">Custom</div>
          <div className="credits">Choose your amount</div>
        </button>
      </div>
    );
  }

  private renderCustomPanel() {
    if (this.state.selectedPackageId !== "custom") return null;
    const { customCents } = this.state;
    const progress = ((customCents - MIN_CUSTOM_CENTS) / (MAX_CUSTOM_CENTS - MIN_CUSTOM_CENTS)) * 100;
    const credits = customCents * CREDITS_PER_CENT;
    const dollars = (customCents / 100).toFixed(2);

    return (
      <div className="aichat-credits__custom">
        <div className="aichat-credits__custom-readout">
          <div>
            <div className="amount">${dollars}</div>
            <div className="credits">{formatCreditAmount(credits)} credits</div>
          </div>
        </div>
        <div className="aichat-credits__custom-range">
          <input
            type="range"
            min={MIN_CUSTOM_CENTS}
            max={MAX_CUSTOM_CENTS}
            step={100}
            value={customCents}
            onChange={this.handleCustomSliderChange}
            style={{ ["--progress" as never]: `${progress}%` } as React.CSSProperties}
          />
        </div>
        <div className="aichat-credits__custom-scale">
          {SLIDER_SCALE_MARKS.map((mark) => {
            const pct = ((mark.cents - MIN_CUSTOM_CENTS) / (MAX_CUSTOM_CENTS - MIN_CUSTOM_CENTS)) * 100;
            return (
              <span key={mark.cents} style={{ left: `${pct}%` }}>
                {mark.label}
              </span>
            );
          })}
        </div>
        <div className="aichat-credits__custom-input-wrap">
          <span className="label">Or enter an exact amount</span>
          <div className="aichat-credits__custom-input">
            <KuikaInput
              type="number"
              size="md"
              min={MIN_CUSTOM_CENTS / 100}
              max={MAX_CUSTOM_CENTS / 100}
              precision={2}
              value={(customCents / 100).toString()}
              onChange={this.handleCustomInputChange}
              leftUnit="$"
            />
          </div>
        </div>
      </div>
    );
  }

  private renderCta() {
    const amountCents = this.getCurrentAmountCents();
    const credits = amountCents * CREDITS_PER_CENT;
    const disabled = this.state.checkoutInProgress || amountCents < MIN_CUSTOM_CENTS || !!this.state.loadError;
    return (
      <div className="aichat-credits__cta">
        <KuikaButton
          data-testid="aiChatbotCreditsCheckoutButton"
          type="primary"
          size="lg"
          loading={this.state.checkoutInProgress}
          disabled={disabled}
          onClick={this.handleCheckout}
        >
          {this.state.checkoutInProgress
            ? "Opening secure checkout…"
            : `Add ${formatCreditAmount(credits)} credits · ${formatCurrencyCents(amountCents)}`}
        </KuikaButton>
      </div>
    );
  }

  private renderBody() {
    const isDomainOwner = !!this.props.context?.userInfo?.isDomainOwner;
    return (
      <>
        <div className="aichat-credits">
          <div className="aichat-credits__intro">
            Your AI chatbot runs on credits shared by everyone in this workspace. Every new workspace starts with a free
            welcome bonus — top up any time to keep the conversation going. Longer chats and more capable models spend
            credits a little faster.
          </div>

          {this.renderHero()}

          {isDomainOwner ? (
            <div className="aichat-credits__section">
              <h2>Add more credits</h2>
              <p className="sub">
                Choose a package or enter the amount you want. Checkout is handled securely by Stripe — credits land in
                your workspace as soon as the payment is confirmed.
              </p>
              {this.renderPackages()}
              {this.renderCustomPanel()}
              {this.renderCta()}
            </div>
          ) : (
            <div className="aichat-credits__section aichat-credits__section--readonly">
              <p className="sub" data-testid="aiChatbotCreditsOwnerOnlyNotice">
                Only the workspace owner can add credits. Ask them to top up if you're running low — everyone in the
                workspace shares the same balance.
              </p>
            </div>
          )}

          <AiChatbotSuccessModal
            isOpen={this.state.successModal.isOpen}
            addedCredits={this.state.successModal.addedCredits}
            newBalance={this.state.successModal.newBalance}
            onClose={this.handleCloseSuccessModal}
          />
        </div>
        {this.state.verifyingPayment && (
          <div className="aichat-credits__verifying" role="status" aria-live="polite">
            <div className="aichat-credits__verifying-card">
              <KuikaLoader size="lg" decorative className="aichat-credits__verifying-spinner" />
              <div className="aichat-credits__verifying-title">Confirming your payment…</div>
              <div className="aichat-credits__verifying-body">
                This usually takes a few seconds. Please don't close this window — your credits will land shortly.
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  render() {
    // Hosted inside the tabbed AI Chatbot shell — the shell owns the frame + header.
    if (this.props.showHeader === false) {
      return this.renderBody();
    }

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="sparkles" type="datasource-icons" />
            <KuikaH4 data-testid="aiChatbotCreditsPageTitle">AI Chatbot Credits</KuikaH4>
          </div>
        </div>
        {this.renderBody()}
      </div>
    );
  }
}

// Credits CONTENT for the tabbed "AI Chatbot" shell (`ai-chatbot-panel.tsx`) —
// render with `showHeader={false}` so the shell owns the frame + header. All
// credits logic (Stripe verify retry, checkout, success modal) stays in here.
export const AiChatbotCreditsBody = withContext<AiChatbotCreditsPanelOwnProps>(
  withHistory<AiChatbotCreditsPanelOwnProps>(AiChatbotCreditsPanel)
);

// Same panel WITHOUT the management shell (ManagementLeftMenu + ManagementLeftPanel
// that withManagement injects). For embedding the credits UI on its own — e.g.
// the chat panel's buy-credits modal — where the full settings chrome would read
// as a right-side drawer. The panel itself only needs history + context props.
// Default `showHeader` (true) keeps this variant byte-identical to before the
// tabbed shell existed. The old default export (the withManagement-wrapped page)
// was replaced by the shell in `ai-chatbot-panel.tsx`.
export const AiChatbotCreditsPanelEmbeddable = withContext<{}>(withHistory<{}>(AiChatbotCreditsPanel));
